import { TelemetryLoggerService } from "./logger-service";
import { TelemetryEventPusher } from "./telemetry-event-pusher";
import { HttpCallbackArgWithSdTracking } from "./http-interceptor";
import { ServiceDeskConfig } from "../../ServiceDesk/Startup/serviceDeskConfig";
import { ContextService, PresenceDataKey } from "../../ServiceDesk/sdServices/context/context-service";
import { TelemetryReadytoSendService } from "../../ServiceDesk/sdServices/telemetry-ready-to-send-service";
import { GlobalTelemetryConstants } from "./telemetry-constants";
import * as Jsll from "./jsll-types";

/**
 * interface for page action telemetry event with overridable content blob part
 */
export interface ContentPageAction {
  contentId: string;
  slotNumber: number;
  contentName: string;
}

/**
 * interface for custom property bag that can be used in contentTags/pageTags telemetry event
 */
export interface CustomKeyValues {
  [key: string]: string;
}

/**
 * interface for content load telemetry event with overridable content blob part
 */
export interface ContentLoadEvent {
  contentName: string;
  contentDescription?: string;
  durationInMs: number;
  sdVersion: string;
  serviceName?: string;
}

/**
 * interface for page action event telemetry event
 */
export interface PageActionEvent extends TelemetryEvent {
  actionType?: string;
  behavior?: any;
  content?: CustomKeyValues;
}

/**
 * interface for sceanrio Telemetry
 * These would be embedded within pageTags metadata awa implementaiton
 */
export interface ScenarioTelemetry {
  /**
   * Name of the scenario
   */
  scenario?: string;
  /**
   * Name of the scenario step
   */
  scenarioStep?: string;
  /**
   * Any scenario error code
   */
  scenarioError?: string;
  /**
   * Indicates success/failure of scenario
   */
  isSuccess?: boolean;
  /**
   * Time taken for the scenario
   */
  durationMs?: number;

  /**
   *additional scenario key values
   */
  additionalMetadata?: CustomKeyValues;
}

/**
 * interface for Telemetry Event
 */
export interface TelemetryEvent {
  /**
   * Page Name
   */
  pageName?: string;
  /**
   * Page tags for the request
   */
  pageTags?: Jsll.PageTags;
  /**
   * Service Name allows to set part A javascript extension.
   */
  serviceName?: string;
  /**
   * scenario telemetry data
   */
  scenarioMetadata?: ScenarioTelemetry;
}

export interface OutgoingQosEventTelemetry extends TelemetryEvent {
  /**
   * The mnemonic name of the dependency operation
   */
  dependencyOperationName?: string;
  /**
   * The name of the service
   */
  dependencyName?: string;
  /**
   * Warning or error message no PII information
   */
  message?: string;
  /**
   * The mnemonic name of the current operation
   */
  operationName?: string;
}

export interface OutgoingQosEventOverride extends OutgoingQosEventTelemetry {
  isSuccess?: boolean;
  requestContent?: string | {};
  responseContent?: string | {};
}

export interface OutgoingQosEvent extends OutgoingQosEventOverride {
  cV?: string;
  dependencyOperationName: string;
  dependencyOperationVersion?: string;
  dependencyName: string;
  dependencyType?: string;
  isSuccess: boolean;
  latencyMs: number;
  operationName: string;
  requestMethod: string;
  responseStatusCode: string;
  responseContentType?: string;
  responseSizeBytes?: number;
  requestFromCache?: boolean;
  requestId?: string;
  requestProtocol: string;
  retryCount?: number;
  serviceErrorCode?: number;
  targetUri: string;
}

export interface ContentUpdateEvent extends TelemetryEvent {
  actionType?: string;
  behavior?: any;
  content?: CustomKeyValues;
  pageLoadTime?: number;
}

/**
 * A request to push an event, can have partB and/or partC data on it.
 * Telemetry event requests have no base implementation, but we use this interface to keep our signatures clean.
 */
export interface EventRequest {
  /**
   * PartB telemetry data if needed for this event.
   */
  partB?: {};
  /**
   * PartC telemetry data if needed for this event.
   */
  partC?: {};
  /**
   * The correlation vector that should be used on the event request.
   */
  cV?: string;
  /**
   * The service name of javascript extension in Part A
   */
  serviceName?: string;
}

/**
 * PartC can be used in place of EventRequests in some places, if you do this you should expect your PartC to be
 *  automatically wrapped as an EventRequest.
 */
export interface PartC {}

/**
 * The publically exposed interface for telemetry events in ServiceDesk.
 */
export interface TelemetryService {
  /**
   * Pushes a single event with a given event name and event data.
   * @param eventName The namespace qualified event name.
   * @param context Either an EventRequest (object with parts B & C) or a PartC object.
   * **ANY JSON Objects contained within partB/partC need to stringified being being sent as context parameter
   */
  pushEvent(eventName: string, context: EventRequest & PartC): void;
  /**
   * @deprecated use page action event
   *  Pushes an event specific from agent feedback
   * @param type of feedback (service desk experience area e.g. email, case, insights ect.)
   * @param data javascript object containing loosely defined data as specified by the consumer
   */
  pushAgentFeedbackEvent(type: string, data: any, serviceName?: string): void;
  /**
   * Event to push information around/regarding errors/exceptions that take place in SD
   * @param exception explanation of error/exception message
   * @param code short code to allow for easily identifying errors/exceptions
   * @param stackTrace complete code stack trace of error/exception
   * @param data javascript object containing loosely defined data as specified by the consumer
   * @param isUnhandled boolean to describe if error broke the application or if it was able to continue otherwise
   * @param name that is provided as name string when exception is raised
   * @param severity a number from 0 - 3 indicating how halting the exception was 0 highest, 3 being lowest
   * @param errorType what the error was, an "Injection" error, a "General" error, a "Playback" error, etc.
   * @param lineNumber where the error/exception occured in code
   * @param isDisplayed whether a message was shown to the end user indicating the error (this is not for console.error displays)
   * @param errorMethod the method or function with the error/exception took place in code
   */
  pushErrorEvent(
    exception: {} | string,
    code: string,
    stackTrace: {} | string,
    data: {} | string,
    isUnhandled: boolean,
    name?: string,
    severity?: number,
    errorType?: string,
    lineNumber?: number,
    isDisplayed?: boolean,
    errorMethod?: string,
    serviceName?: string,
    logImmediately?: boolean
  ): void;
  /**
   * Event for providing a method to push information via telemetry
   * @param type to describe what information is being sent (Information = 0, Warning = 1, Other = 2)
   * @param data javascript object containing loosely defined data as specified by the consumer
   */
  pushInformationEvent(type: any, data: {}, serviceName?: string, logImmediately?: boolean): void;
  /**
   * Pushes event to capture telemetry around outgoing api service request calls
   * @param httpCallbackArg ng.IHttpPromiseCallbackArg with sdStartTime and sdEndTime for outgoing api service request calls
   */
  pushOutgoingRequestEvent(httpCallbackArg: HttpCallbackArgWithSdTracking): void;
  /**
   * Push event to capture any completed page navigated actions
   * @param pageName the friendly name given to represent a specific page
   * @param referrerUri uri preceding navigation to current uri
   * @param viewId friendly name of uri root after ServiceDesk/ or ServiceDesk if nothing else (ie old concept of appId/subAppId)
   * @param data javascript object containing loosely defined data as specified by the consumer
   */
  pushPageViewEvent(pageName: string, referrerUri: string, viewId?: string, data?: {}): void;

  /**
   * Pushes a page action event, performed on the submitted element.
   */
  pushPageActionElementEvent(
    element: ng.IRootElementService,
    behavior?: any,
    content?: ContentPageAction,
    actionType?: string,
    pageTags?: CustomKeyValues,
    pageMetaTags?: CustomKeyValues,
    serviceName?: string
  ): void;
  /**
   * Retrieves the incremented value of the current correlation vector. The method should be used when retrieving a correlation vector to use in an outgoing request.
   */
  getIncrementedCorrelationVector(): string;

  /**
   * Retrieves the current correlation vector. This vector should not be retrieved with the purpose of modifying its value (increment / extend / etc).
   */
  getCorrelationVector(): string;

  /**
   * Pushes a content update event to capture any completed content loaded actions.
   *  @param contentName Name or an identifier that can be logged as part of this telemetry action.
   *  @param contentDescription Description that can be logged as part of this telemetry action.
   *  @param overrideContentLoadDefaultStartTime Used to override the start time for content load actions when user wants to track an action that is not triggered by routeStateChange.
   */

  pushContentLoadEvent(
    contentName: string,
    contentDescription?: string,
    overrideContentLoadDefaultStartTime?: Date,
    serviceName?: string
  ): void;

  /**
   * Pushes a content update event to capture any completed content loaded actions with a specific duration.
   *  @param contentName Name or an identifier that can be logged as part of this telemetry action.
   *  @param durationInMs The duration of the event to report in milliseconds
   *  @param contentDescription Description that can be logged as part of this telemetry action.
   */
  pushContentLoadEventDuration(
    contentName: string,
    durationInMs: number,
    contentDescription?: string,
    serviceName?: string
  ): void;
  /**
   * Pushes a page action event to capture an action done on a page like button click or scroll.
   * @param actionType The type of UI action.
   * @param behaviour Type of UX behaviour like Search.
   * @param pageTags Any tag which can be set with the event specific to page action
   * @param pageMetaTags Any metatag which can be set with the event specific to page action
   * @param data Any data that needs to be logged with the event. AgentEmail/AgentPuid will be added by default.
   * **For more information, visit https://microsoft.sharepoint.com/sites/mscomdata/Pages/awa-page-metadata-collection.aspx
   */
  pushPageActionEvent(
    actionType?: string,
    behavior?: any,
    pageMetaTags?: CustomKeyValues,
    data?: CustomKeyValues,
    pageTags?: Jsll.PageTags,
    serviceName?: string
  ): void;

  /**
   * Pushes a outgoing request qos event like XHR requests, external dependency.
   * @param {Services.OutgoingQosEvent} event with outgoing qos metadata to be logged
   */
  pushOutgoingQosEvent(event: OutgoingQosEvent): void;

  /**
   *
   * Pushes content update event like performance measures, updates to content on the page etc.
   * @param {Services.ContentUpdateEvent} event with metadata to capture for content update
   * @memberof TelemetryService
   */
  pushContentUpdateEvent(event: ContentUpdateEvent, pageMetaTags?: CustomKeyValues): void;

  /**
   * Sets/ gets the serviceName used for identifying telemetry for a specific team/component
   * @param {string} name service name
   */
  serviceName: string;
}

export interface InternalEventRequest extends EventRequest {
  cV: string;
}

export class TelemetryServiceImplementation implements TelemetryService {
  private telemetryContext: Jsll.ICoreData = {};
  private routeChangeStartTime: Date = new Date();
  private telemetryReadytoSendService: TelemetryReadytoSendService;

  // @ngInject
  constructor(
    private $location: ng.ILocationService,
    private loggerService: TelemetryLoggerService,
    private telemetryEventPusher: TelemetryEventPusher,
    private $rootScope: ng.IRootScopeService,
    private contextService: ContextService,
    private $injector: ng.auto.IInjectorService,
    private $window: ng.IWindowService,
    private sdConfig: ServiceDeskConfig,
    private telemetryConstants: GlobalTelemetryConstants
  ) {
    this.telemetryContext = loggerService.getTelemetryContext() || {};
    this.telemetryContext.pageName = "sdSystem.loading";
    this.telemetryContext.pageTags = this.telemetryContext.pageTags || {};
    this.extendTelemetryContext();
  }

  public pushEvent(eventName: string, context: EventRequest & PartC, logImmediately?: boolean): any {
    this.extendTelemetryContext();
    // Treat an arbitrary data object as if it"s just Part C. We do this for the sake of our API consumers.
    if (context && !context.partB && !context.partC) {
      context = {
        partC: context
      };
    }

    return this.telemetryEventPusher.pushEvent(eventName, context, logImmediately);
  }

  public pushAgentFeedbackEvent(type: string, data: any, serviceName?: string): any {
    this.extendTelemetryContext();
    let overrides: Jsll.IOverrideValues = {};
    overrides.behavior = this.telemetryConstants.pageActionBehavior.ReportReview;
    overrides.content = this.stringifyIfNeeded({ feedbackType: type, feedbackData: data });
    overrides.serviceName = this.getServiceName(serviceName);
    this.loggerService.captureContentPageAction(overrides);
  }

  public pushErrorEvent(
    exception: {} | string,
    code: string,
    stackTrace: {} | string,
    data: {} | string,
    isUnhandled = false,
    name?: string,
    severity?: number,
    errorType?: string,
    lineNumber?: number,
    isDisplayed?: boolean,
    errorMethod?: string,
    serviceName?: string,
    logImmediately?: boolean
  ): any {
    this.extendTelemetryContext();
    let partB = {
      errorName: name || "no-error-name",
      errorMessage: this.stringifyIfNeeded(exception) || "no-error-message",
      severity: severity || 3,
      errorType: errorType || "General",
      lineNumber: lineNumber || 0,
      isDisplayed: isDisplayed,
      errorLocation: this.stringifyIfNeeded(stackTrace) || "no-error-location",
      errorMethod: errorMethod || "no-error-method"
    };
    let partC = {
      code: code,
      detailedErrorData: this.stringifyIfNeeded(data),
      isUnhandled: isUnhandled,
      pageName: this.telemetryContext.pageName,
      pageTags: this.stringifyIfNeeded(this.telemetryContext.pageTags),
      uri: this.getRequestUri()
    };
    return this.pushEvent(
      this.telemetryConstants.eventsByName.Error,
      {
        partB: partB,
        partC: partC,
        cV: this.getCorrelationVector(),
        serviceName: this.getServiceName(serviceName)
      },
      logImmediately
    );
  }

  public pushInformationEvent(type: any, data: {}, serviceName?: string, logImmediately?: boolean): any {
    this.extendTelemetryContext();
    return this.pushEvent(
      this.telemetryConstants.eventsByName.Information,
      {
        partC: {
          informationType: type,
          informationData: this.stringifyIfNeeded(data),
          pageName: this.telemetryContext.pageName,
          pageTags: this.stringifyIfNeeded(this.telemetryContext.pageTags),
          uri: this.getRequestUri()
        },
        cV: this.getCorrelationVector(),
        serviceName: this.getServiceName(serviceName)
      },
      logImmediately
    );
  }

  public pushOutgoingQosEvent(event: OutgoingQosEvent): any {
    this.extendTelemetryContext();
    return this.pushEvent(this.telemetryConstants.eventsByName.OutgoingRequest, {
      partB: {
        operationName: event.operationName || "Unknown",
        targetUri: this.stringifyIfNeeded(event.targetUri),
        latencyMs: event.latencyMs,
        succeeded: event.isSuccess,
        serviceErrorCode: event.serviceErrorCode || "0",
        responseContentType: event.responseContentType || "",
        protocol: event.requestProtocol,
        protocolStatusCode: event.responseStatusCode || "0",
        dependencyName: event.dependencyName || "Unknown.ServiceDesk",
        dependencyOperationName: event.dependencyOperationName || "Unknown.".concat(event.operationName),
        responseSizeBytes: event.responseSizeBytes || 0,
        requestMethod: event.requestMethod
      },
      partC: {
        pageName: event.pageName || this.telemetryContext.pageName,
        pageTags: this.getPageTags(event.pageTags),
        uri: this.getRequestUri(),
        requestId: event.requestId || "",
        requestContent: this.stringifyIfNeeded(event.requestContent),
        responseContent: this.stringifyIfNeeded(event.responseContent),
        retryCount: event.retryCount,
        requestFromCache: event.requestFromCache
      },
      cV: event.cV,
      serviceName: this.getServiceName(event.serviceName) || undefined
    });
  }

  // This method should be used only by the shell level HTTP interceptor.
  public pushOutgoingRequestEvent(httpCallbackArg: HttpCallbackArgWithSdTracking): any {
    this.extendTelemetryContext();
    let config = httpCallbackArg.config,
      status = httpCallbackArg.status,
      headers = httpCallbackArg.headers,
      data = httpCallbackArg.data;
    let cV = config.cV || config.headers[this.loggerService.getCorrelationVectorHeader()],
      // use 0 for undefined so the .toString() below doesn't throw
      responseStatus = angular.isUndefined(status) ? 0 : status,
      responseContentType = "",
      responseSizeBytes = 0,
      requestId = "",
      requestFromCache = false,
      responseLatency =
        config._sd_start_time && config._sd_end_time
          ? config._sd_end_time.getTime() - config._sd_start_time.getTime()
          : -1;

    let protocolSubstringIndex: number = config.url.indexOf("://");
    let protocol: string;
    if (protocolSubstringIndex != -1) {
      protocol = config.url.substring(0, protocolSubstringIndex).toUpperCase();
    } else {
      protocol = this.$location.protocol().substring(0, config.url.indexOf(":")).toUpperCase();
    }
    if (angular.isFunction(headers)) {
      let contentTypeHeader = headers("Content-Type");
      let contentLengthHeader = headers("Content-Length");
      let requestIdHeader = headers("Request-Id");

      responseContentType = contentTypeHeader == undefined ? "" : contentTypeHeader;
      // Gets the size from the content-length header if available, otherwise tries to stringify response and the the length
      responseSizeBytes =
        contentLengthHeader != undefined
          ? parseInt(contentLengthHeader)
          : data != undefined
          ? JSON.stringify(data).length
          : 0;
      requestId = requestIdHeader == undefined ? "" : requestIdHeader;
    }

    let wasSuccessful = responseStatus > 199 && responseStatus < 300;
    if (config.cache && angular.isFunction(config.cache.info)) {
      let cacheInfo = config.cache.info(config.url);
      if (cacheInfo && cacheInfo.accessed != cacheInfo.created) {
        requestFromCache = true;
      }
    }

    let qosEvent = {
      operationName: config.operationName || "Unknown",
      targetUri: config.url,
      latencyMs: responseLatency,
      isSuccess: wasSuccessful,
      responseContentType: responseContentType,
      requestProtocol: protocol,
      responseStatusCode: status.toString(),
      dependencyName: config.dependencyName || "Unknown.ServiceDesk",
      dependencyOperationName: config.dependencyOperationName || "Unknown.".concat(config.operationName),
      responseSizeBytes: responseSizeBytes || 0,
      requestMethod: config.method,
      requestId: requestId,
      requestContent: this.isContentLoggable(!wasSuccessful || config.includeRequest, httpCallbackArg, config.data),
      responseContent: this.isContentLoggable(config.includeResponse, httpCallbackArg, data),
      retryCount: config.retryCount,
      requestFromCache: requestFromCache,
      cV: cV,
      serviceName: this.getServiceName(config.serviceName) || undefined,
      message: config.message,
      pageTags: this.mergePageTags(config.pageTags, config)
    };

    if (angular.isFunction(config.qosEventCallback) && config.qosEventCallback) {
      try {
        let qosOverride = config.qosEventCallback(httpCallbackArg) || undefined;
        if (qosOverride) {
          qosEvent.operationName = qosOverride.operationName || qosEvent.operationName;
          qosEvent.dependencyName = qosOverride.dependencyName || qosEvent.dependencyName;
          qosEvent.dependencyOperationName = qosOverride.dependencyOperationName || qosEvent.dependencyOperationName;
          qosEvent.requestContent = this.stringifyIfNeeded(qosOverride.requestContent) || qosEvent.requestContent;
          qosEvent.responseContent = this.stringifyIfNeeded(qosOverride.responseContent) || qosEvent.responseContent;
          qosEvent.serviceName = qosOverride.serviceName || qosEvent.serviceName;
          qosEvent.isSuccess = qosOverride.isSuccess == undefined ? qosEvent.isSuccess : qosOverride.isSuccess;
          qosEvent.pageTags = angular.merge(
            {},
            qosEvent.pageTags,
            this.mergePageTags(qosOverride.pageTags, qosOverride)
          );
        }
      } catch (e) {
        qosEvent.message = `${qosEvent.message};${e} `;
      }
    }

    return this.pushOutgoingQosEvent(qosEvent);
  }

  private getRequestUri(): string {
    let uri = this.telemetryContext.requestUri || (window && window.location && window.location.href);
    return this.removeBearerToken(uri);
  }

  private mergePageTags(pageTags: string | {}, telemetryEvent?: TelemetryEvent): {} {
    let pageTagsObject = {};
    let pageMetaTags = {};
    if (typeof pageTags === "string" && pageTags.length > 0) {
      pageTagsObject = JSON.parse(pageTags);
    } else {
      pageTagsObject = pageTags;
    }
    if (telemetryEvent && telemetryEvent.scenarioMetadata && telemetryEvent.scenarioMetadata.scenario) {
      let scenarioMetadata = telemetryEvent.scenarioMetadata;
      let metaTags = {
        scn: scenarioMetadata.scenario,
        scnerror: scenarioMetadata.scenarioError
      };

      if (scenarioMetadata.isSuccess != undefined) {
        (<any>metaTags).isSuccess = scenarioMetadata.isSuccess;
      }

      if (scenarioMetadata.scenarioStep) {
        (<any>metaTags).scnstp = scenarioMetadata.isSuccess;
      }
      if (scenarioMetadata.scenarioError) {
        (<any>metaTags).scnerror = scenarioMetadata.scenarioError;
      }

      pageMetaTags = {
        metaTags: angular.merge({}, metaTags, scenarioMetadata.additionalMetadata)
      };
    }
    return angular.merge({}, this.telemetryContext.pageTags, pageTagsObject, pageMetaTags);
  }

  private getPageTags(pageTags: string | {}): string {
    let pageTagsObject = {};
    if (typeof pageTags === "string" && pageTags.length > 0) {
      pageTagsObject = JSON.parse(pageTags);
    } else {
      pageTagsObject = pageTags;
    }

    let merged = angular.merge({}, this.telemetryContext.pageTags, pageTagsObject);
    return JSON.stringify(merged);
  }

  private isContentLoggable(expression: boolean | Function, httpCallbackArg: HttpCallbackArgWithSdTracking, data: any) {
    let shouldIncludeContent = expression;
    if (typeof expression === "function") {
      shouldIncludeContent = expression(httpCallbackArg);
    }
    return shouldIncludeContent === true
      ? this.stringifyIfNeeded(data).substr(0, 2000)
      : this.telemetryConstants.piiPlaceholder;
  }

  public pushPageViewEvent(currentUri?: string, referrerUri?: string, viewId?: string): any {
    this.telemetryContext.requestUri = this.removeBearerToken(currentUri);
    this.telemetryContext.referrerUri = this.removeBearerToken(referrerUri);
    this.extendTelemetryContext(viewId);
    this.loggerService.capturePageView(this.telemetryContext.pageName, this.telemetryContext.referrerUri);
  }

  public pushPageActionElementEvent(
    element: ng.IRootElementService,
    behavior?: any,
    content?: ContentPageAction,
    actionType?: string,
    pageTags?: CustomKeyValues,
    pageMetaTags?: CustomKeyValues,
    serviceName?: string
  ): void {
    if (pageMetaTags) {
      if (!pageTags) {
        pageTags = <CustomKeyValues>{};
      }
      (<Jsll.PageTags>pageTags)["metaTags"] = pageMetaTags;
    }
    this.loggerService.capturePageAction(
      element,
      behavior,
      content,
      actionType,
      pageTags,
      this.getServiceName(serviceName)
    );
  }

  private stringifyIfNeeded(data: any) {
    if (typeof data === "string") {
      data = this.removeBearerToken(data);
      return data || "";
    } else {
      return data ? angular.toJson(data) : "";
    }
  }

  public getCorrelationVector() {
    // Keeping logger service behind a facade as we can/should retire it.
    return this.loggerService.getCorrelationVector();
  }

  public getIncrementedCorrelationVector() {
    // Keeping logger service behind a facade as we can/should retire it.
    return this.loggerService.incrementCorrelationVector();
  }

  public initializeTimeOnRouteChangeStart() {
    this.telemetryReadytoSendService = this.$injector.get<TelemetryReadytoSendService>("telemetryReadytoSendService");
    this.$rootScope.$on("$stateChangeStart", (evt, to) => {
      this.routeChangeStartTime = new Date();
      // serviceName should not be change during the state change, that's handle on changeSuccess
      // only set toDestination's serviceName when serviceName is not set, like launching of SD
      if (!this.telemetryContext.serviceName) {
        this.telemetryContext.serviceName = this.getServiceNameFromState(to);
        this.telemetryContext.pageName = to.name;
        this.extendTelemetryContext();
      }
    });

    this.$rootScope.$on("$stateChangeSuccess", (evt, to) => {
      this.telemetryContext.serviceName = this.getServiceNameFromState(to);
      this.telemetryContext.pageName = to.name;
      this.extendTelemetryContext();
    });

    this.$rootScope.$on("$locationChangeSuccess", (event: ng.IAngularEvent, absNewUrl: string, absOldUrl: string) => {
      this.pushPageViewEvent(absNewUrl, absOldUrl);
    });

    this.telemetryReadytoSendService.whenReady().then(() => {
      this.extendTelemetryContext();
    });
  }

  private getServiceName(serviceName?: string) {
    return serviceName || this.telemetryContext.serviceName;
  }

  private getServiceNameFromState(state: any): string {
    if (state.serviceName) {
      return state.serviceName;
    }
    if (state && state.name) {
      return state.name.split(".")[0];
    }
    return state;
  }

  private extendTelemetryContext(viewId?: string) {
    if (!this.telemetryContext.pageName) {
      this.telemetryContext.pageName = this.loggerService.getPageName();
    }
    if (viewId || this.telemetryContext.pageName) {
      this.telemetryContext.pageTags["viewId"] = viewId || this.telemetryContext.pageName;
    }

    if (this.contextService.appContext.agentUpn) {
      this.loggerService.setAuthUserId(this.contextService.appContext.agentUpn);
    }

    if (this.contextService.appContext) {
      this.telemetryContext.pageTags["agentId"] =
        this.contextService.appContext.agentId || this.telemetryConstants.unKnown;
      this.telemetryContext.pageTags["vendorSiteId"] =
        this.contextService.appContext.vendorSiteId || this.telemetryConstants.unKnown;
      this.telemetryContext.pageTags["vendorSiteName"] =
        this.contextService.appContext.vendorSiteName || this.telemetryConstants.unKnown;
    }
    this.telemetryContext.pageTags["isInFrame"] =
      this.$window.isInFrame == undefined ? this.telemetryConstants.unKnown : this.$window.isInFrame;
    this.telemetryContext.pageTags["browserMode"] = this.$window.browserMode || this.telemetryConstants.unKnown;

    let presence = this.contextService.getExtendedContextData(PresenceDataKey);
    this.telemetryContext.pageTags["presence"] = presence || this.telemetryConstants.unKnown;
    this.telemetryContext.requestUri = this.getRequestUri();

    this.loggerService.extendTelemetryContext(this.telemetryContext);
  }

  public pushContentLoadEvent(
    contentName: string,
    contentDescription?: string,
    overrideContentLoadDefaultStartTime?: Date,
    serviceName?: string
  ): void {
    this.extendTelemetryContext();
    this.routeChangeStartTime = overrideContentLoadDefaultStartTime
      ? overrideContentLoadDefaultStartTime
      : this.routeChangeStartTime;
    let durationInMs = new Date().getTime() - this.routeChangeStartTime.getTime();
    this.pushContentLoadEventDuration(contentName, durationInMs, contentDescription, serviceName);
  }

  // To be used to log events related to actions on page like click/submit etc.
  public pushPageActionEvent(
    actionType?: string,
    behavior?: any,
    pageMetaTags?: CustomKeyValues,
    data?: CustomKeyValues,
    pageTags?: Jsll.PageTags,
    serviceName?: string
  ) {
    this.extendTelemetryContext();
    let allPageTags: Jsll.PageTags;
    if (pageMetaTags) {
      // construct a page tags object
      allPageTags = { metaTags: pageMetaTags };
      for (let key in pageTags) {
        // flatten all keys from pageTags
        allPageTags[key] = pageTags[key];
      }
    } else {
      allPageTags = pageTags;
    }
    if (!allPageTags) {
      allPageTags = {};
    }

    let event = {
      actionType: actionType,
      behavior: behavior,
      pageTags: allPageTags,
      content: data
    } as PageActionEvent;

    event.serviceName = this.getServiceName(serviceName);

    this.loggerService.captureContentPageAction(event);
  }

  public pushContentLoadEventDuration(
    contentName: string,
    durationInMs: number,
    contentDescription?: string | {},
    serviceName?: string
  ): void {
    this.extendTelemetryContext();
    // If the time was over the max threshold something is likely wrong with the measurement
    if (durationInMs > this.telemetryConstants.maxDurationMsThreshold) {
      this.pushInformationEvent(
        this.telemetryConstants.informationTypes.Warning,
        `Attempted to report a content load time over the max threshold (${this.telemetryConstants.maxDurationMsThreshold}ms), for content ${contentName}`,
        serviceName
      );
      return;
    }

    let telemetryEvent: ContentLoadEvent = {
      contentName: contentName,
      contentDescription:
        typeof contentDescription === "string" ? contentDescription : JSON.stringify(contentDescription),
      durationInMs: durationInMs,
      sdVersion: this.sdConfig.version,
      serviceName: this.getServiceName(serviceName)
    };

    this.loggerService.captureContentLoadEvent(telemetryEvent);
  }

  public pushContentUpdateEvent(event: ContentUpdateEvent, pageMetaTags?: CustomKeyValues): void {
    this.extendTelemetryContext();
    event.actionType = event.actionType || this.telemetryConstants.pageActionType.auto;
    event.behavior = event.behavior || this.telemetryConstants.pageActionBehavior.PageScroll;
    event.pageTags = event.pageTags || {};
    if (pageMetaTags) {
      event.pageTags.metaTags = angular.merge({}, event.pageTags.metaTags, pageMetaTags);
    }
    this.loggerService.captureContentUpdate(event);
  }

  public set serviceName(name: string) {
    if (name) {
      this.telemetryContext.serviceName = name;
      this.loggerService.extendTelemetryContext({ serviceName: name });
    }
  }

  public get serviceName() {
    let context = this.loggerService.getTelemetryContext();
    if (context) {
      return context.serviceName;
    }
  }

  private removeBearerToken(bearerTokenString: string) {
    if (bearerTokenString && typeof bearerTokenString === "string") {
      // removes Bearer token from given string
      let bearerStr = "Bearer";
      let idTokenStr = "id_token";
      let accessTokenStr = "access_token";
      let startIndex: number = bearerTokenString.indexOf(bearerStr);
      if (startIndex >= 0) {
        let endIndex: number = bearerTokenString.indexOf('"', startIndex);
        let subString: string = bearerTokenString.substring(startIndex, endIndex);
        bearerTokenString = bearerTokenString.replace(subString, "");
      }
      if (bearerTokenString.indexOf(idTokenStr) >= 0) {
        bearerTokenString = bearerTokenString.split(idTokenStr)[0];
      } else if (bearerTokenString.indexOf(accessTokenStr) >= 0) {
        bearerTokenString = bearerTokenString.split(accessTokenStr)[0];
      }
    }
    return bearerTokenString;
  }

  // calculate network speed- kb per second based on totalTransferSize and totalResponseTime
  public setDataTransferRate(dataTransferRate: number): void {
    this.telemetryContext.pageTags["transferRateKbPerSecond"] = dataTransferRate.toFixed(3);
  }
}
